import { NgClass } from '@angular/common';
import { Component, ElementRef, inject, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { I18nService } from '../../core/i18n.service';
import { GoogleMapsLoaderService } from './google-maps-loader.service';
import { HomeLocationSearchService } from './home-location-search.service';
import {
  LocationSearchHistoryItem,
  LocationSearchHistoryService,
} from './location-search-history.service';

const LOCATION_SEARCH_RADIUS_KM = 5;
const LOCATION_SEARCH_DEBOUNCE_MS = 250;

interface LocationSearchSuggestion {
  id: string;
  title: string;
  subtitle: string;
  text: string;
  prediction: google.maps.places.PlacePrediction;
}

@Component({
  selector: 'app-location-search',
  standalone: true,
  imports: [FormsModule, NgClass],
  templateUrl: './location-search.component.html',
  styleUrl: './location-search.component.scss',
})
export class LocationSearchComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);
  private readonly homeLocationSearch = inject(HomeLocationSearchService);
  private readonly locationSearchHistory = inject(LocationSearchHistoryService);
  protected readonly i18n = inject(I18nService);

  @Input() panelClass = '';
  @ViewChild('searchTriggerInput') private searchTriggerInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchCloseButton') private searchCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('searchSubmitButton') private searchSubmitButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('searchScreenInput') private searchScreenInput?: ElementRef<HTMLInputElement>;

  private locationSearchTimer?: ReturnType<typeof setTimeout>;
  private locationSearchRequestId = 0;
  private locationSearchSessionToken: google.maps.places.AutocompleteSessionToken | null = null;
  private previousBodyOverflow = '';

  searchQuery = '';
  isSearchScreenOpen = false;
  isLocationSearching = false;
  locationSearchError = '';
  showLocationSuggestions = false;
  locationSuggestions: LocationSearchSuggestion[] = [];
  recentLocationSearches: LocationSearchHistoryItem[] = this.locationSearchHistory.getRecent();

  protected get showingRecentLocationSearches(): boolean {
    return this.showLocationSuggestions && this.searchQuery.trim().length === 0 && this.recentLocationSearches.length > 0;
  }

  ngOnDestroy(): void {
    this.clearLocationSearchTimer();
    this.restoreBodyScroll();
  }

  openSearchScreen(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const wasOpen = this.isSearchScreenOpen;
    this.isSearchScreenOpen = true;
    this.locationSearchError = '';
    if (!wasOpen) {
      this.lockBodyScroll();
    }

    if (!this.searchQuery.trim()) {
      this.showRecentLocationSearches();
    } else if (this.locationSuggestions.length > 0) {
      this.showLocationSuggestions = true;
    }

    window.setTimeout(() => {
      this.searchScreenInput?.nativeElement.focus();
    });
  }

  closeSearchScreen(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.isSearchScreenOpen = false;
    this.showLocationSuggestions = false;
    this.restoreBodyScroll();

    window.setTimeout(() => {
      this.searchTriggerInput?.nativeElement.focus();
    });
  }

  trapSearchScreenFocus(event: Event): void {
    if (!this.isSearchScreenOpen) {
      return;
    }

    const keyboardEvent = event as KeyboardEvent;
    const candidates: Array<HTMLElement | undefined> = [
      this.searchCloseButton?.nativeElement,
      this.searchSubmitButton?.nativeElement,
      this.searchScreenInput?.nativeElement,
    ];
    const focusableElements = candidates.filter((element): element is HTMLElement => Boolean(element));

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!firstElement || !lastElement) {
      return;
    }

    if (keyboardEvent.shiftKey && document.activeElement === firstElement) {
      keyboardEvent.preventDefault();
      lastElement.focus();
      return;
    }

    if (!keyboardEvent.shiftKey && document.activeElement === lastElement) {
      keyboardEvent.preventDefault();
      firstElement.focus();
    }
  }

  onLocationSearchInput(): void {
    this.locationSearchError = '';
    this.clearLocationSearchTimer();

    const query = this.searchQuery.trim();
    if (!query) {
      this.locationSuggestions = [];
      this.locationSearchSessionToken = null;
      this.showRecentLocationSearches();
      return;
    }

    if (query.length < 2) {
      this.locationSuggestions = [];
      this.showLocationSuggestions = false;
      this.locationSearchSessionToken = null;
      return;
    }

    this.locationSearchTimer = setTimeout(() => {
      void this.fetchLocationSuggestions(query);
    }, LOCATION_SEARCH_DEBOUNCE_MS);
  }

  onLocationSearchFocus(): void {
    if (!this.searchQuery.trim()) {
      this.showRecentLocationSearches();
      return;
    }

    if (this.locationSuggestions.length > 0) {
      this.showLocationSuggestions = true;
    }
  }

  hideLocationSuggestionsSoon(): void {
    if (!this.isSearchScreenOpen) {
      window.setTimeout(() => {
        this.showLocationSuggestions = false;
      }, 140);
    }
  }

  async searchLocation(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    const query = this.searchQuery.trim();
    if (!query || this.isLocationSearching) {
      return;
    }

    if (this.locationSuggestions.length > 0) {
      await this.selectLocationSuggestion(this.locationSuggestions[0]);
      return;
    }

    await this.fetchLocationSuggestions(query);
    if (this.locationSuggestions.length > 0) {
      await this.selectLocationSuggestion(this.locationSuggestions[0]);
      return;
    }

    this.locationSearchError = this.i18n.t('shell.locationSearchError');
  }

  async selectLocationSuggestion(suggestion: LocationSearchSuggestion, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    this.isLocationSearching = true;
    this.locationSearchError = '';

    try {
      const place = suggestion.prediction.toPlace();
      await place.fetchFields({ fields: ['location', 'formattedAddress', 'displayName'] });
      const location = place.location;

      if (!location) {
        throw new Error('Location was not found');
      }

      const selectedLocation = {
        id: suggestion.id,
        label: place.formattedAddress || place.displayName || suggestion.text,
        latitude: location.lat(),
        longitude: location.lng(),
      };

      await this.router.navigate(['/']);
      this.applySelectedLocation(selectedLocation);
      this.recentLocationSearches = this.locationSearchHistory.save(selectedLocation);
      this.locationSearchSessionToken = null;
      this.closeSearchScreen();
    } catch {
      this.locationSearchError = this.i18n.t('shell.locationSearchError');
    } finally {
      this.isLocationSearching = false;
    }
  }

  async selectRecentLocationSearch(item: LocationSearchHistoryItem, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();

    this.isLocationSearching = true;
    this.locationSearchError = '';

    try {
      await this.router.navigate(['/']);
      this.applySelectedLocation(item);
      this.recentLocationSearches = this.locationSearchHistory.save(item);
      this.locationSearchSessionToken = null;
      this.closeSearchScreen();
    } finally {
      this.isLocationSearching = false;
    }
  }

  private async fetchLocationSuggestions(query: string): Promise<void> {
    const requestId = ++this.locationSearchRequestId;
    this.isLocationSearching = true;

    try {
      const libraries = await this.googleMapsLoader.load();
      if (!libraries) {
        throw new Error('Google Maps is unavailable');
      }

      this.locationSearchSessionToken ??= new libraries.AutocompleteSessionToken();
      const response = await libraries.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: this.locationSearchSessionToken,
        region: 'us',
      });

      if (requestId !== this.locationSearchRequestId || this.searchQuery.trim() !== query) {
        return;
      }

      this.locationSuggestions = response.suggestions
        .map((suggestion): LocationSearchSuggestion | null => {
          const prediction = suggestion.placePrediction;
          if (!prediction) {
            return null;
          }

          return {
            id: prediction.placeId,
            title: prediction.mainText?.text || prediction.text.text,
            subtitle: prediction.secondaryText?.text || '',
            text: prediction.text.text,
            prediction,
          };
        })
        .filter((suggestion): suggestion is LocationSearchSuggestion => suggestion !== null)
        .slice(0, 5);
      this.showLocationSuggestions = this.locationSuggestions.length > 0;
    } catch {
      if (requestId === this.locationSearchRequestId) {
        this.locationSuggestions = [];
        this.showLocationSuggestions = false;
        this.locationSearchError = this.i18n.t('shell.locationSearchError');
      }
    } finally {
      if (requestId === this.locationSearchRequestId) {
        this.isLocationSearching = false;
      }
    }
  }

  private clearLocationSearchTimer(): void {
    if (this.locationSearchTimer) {
      clearTimeout(this.locationSearchTimer);
      this.locationSearchTimer = undefined;
    }
  }

  private showRecentLocationSearches(): void {
    this.recentLocationSearches = this.locationSearchHistory.getRecent();
    this.showLocationSuggestions = this.recentLocationSearches.length > 0;
  }

  private applySelectedLocation(item: LocationSearchHistoryItem): void {
    this.searchQuery = item.label;
    this.locationSuggestions = [];
    this.showLocationSuggestions = false;
    this.homeLocationSearch.focusLocation({
      latitude: item.latitude,
      longitude: item.longitude,
      label: item.label,
      radiusKm: LOCATION_SEARCH_RADIUS_KM,
    });
  }

  private lockBodyScroll(): void {
    if (typeof document === 'undefined') {
      return;
    }

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private restoreBodyScroll(): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.style.overflow = this.previousBodyOverflow;
  }
}
